import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import dayjs from "dayjs";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useAppVersions,
  useArchiveAppVersion,
  useCreateAppVersion,
  useSetCurrentAppVersion,
  useUpdateAppVersion,
} from "@/hooks/useAppVersions";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";

import {
  AppVersion,
  AppVersionCreatePayload,
  AppVersionPlatform,
  AppVersionUpdatePayload,
} from "@/domain/app-versions.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import AppVersionFilters, { Filters } from "@/ui/content/appVersions/Filters";
import AppVersionModal from "@/ui/modals/appVersions/AppVersionModal";

const PLATFORM_LABELS: Record<string, string> = {
  ios: "iOS",
  android: "Android",
  backoffice: "Backoffice",
};

const DEFAULT_FILTERS: Filters = {
  sortField: "createdAt",
  sortOrder: -1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  platform: "",
  isCurrent: "",
};

const AppVersionsPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    appVersion?: AppVersion;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    appVersion?: AppVersion;
  }>({ open: false });

  const [setCurrentAlert, setSetCurrentAlert] = useState<{
    open: boolean;
    appVersion?: AppVersion;
  }>({ open: false });

  const {
    appVersions,
    isLoadingAppVersions,
    isFetchingAppVersions,
    refetchAppVersions,
  } = useAppVersions({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    platform: filters.platform,
    isCurrent: filters.isCurrent,
  });

  const createAppVersion = useCreateAppVersion();
  const updateAppVersion = useUpdateAppVersion();
  const setCurrentMutation = useSetCurrentAppVersion();
  const archiveAppVersion = useArchiveAppVersion();
  const createAuditLog = useCreateAuditlog();

  const isSaving = createAppVersion.isPending || updateAppVersion.isPending;

  useEffect(() => {
    setPageTitle("App Versions");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.APP_VERSIONS,
    option: OPTIONS.APP_VERSIONS_LIST,
    actionCode: ACTION_CODE.WEB_APP_VER_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of app versions",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchAppVersions();
  }, [
    debouncedSearch,
    filters.platform,
    filters.isCurrent,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.APP_VERSIONS,
        option: OPTIONS.APP_VERSIONS_LIST,
        actionCode: ACTION_CODE.WEB_APP_VER_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of app versions",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, appVersion: undefined });
  };

  const handleOpenEdit = (appVersion: AppVersion) => {
    setModalState({ open: true, appVersion });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, appVersion: undefined });
  };

  const handleSave = async (
    data: AppVersionCreatePayload | AppVersionUpdatePayload,
    isEdit: boolean
  ) => {
    const editing = modalState.appVersion;

    try {
      if (isEdit && editing) {
        await updateAppVersion.mutateAsync({
          id: editing._id,
          payload: data as AppVersionUpdatePayload,
        });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.APP_VERSIONS,
          option: OPTIONS.APP_VERSION_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_APP_VER_EDIT,
          action: "When editing an app version",
          detail: `Edited app version ${editing.platform} ${editing.version}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The app version has been updated.", "success");
      } else {
        const payload = data as AppVersionCreatePayload;
        await createAppVersion.mutateAsync(payload);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.APP_VERSIONS,
          option: OPTIONS.APP_VERSION_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_APP_VER_ADD,
          action: "When creating an app version",
          detail: `Created app version ${payload.platform} ${payload.version}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The app version has been created.", "success");
      }

      handleCloseModal();
      refetchAppVersions();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmSetCurrent = async () => {
    const target = setCurrentAlert.appVersion;
    if (!target) return;

    try {
      await setCurrentMutation.mutateAsync({
        id: target._id,
        platform: target.platform as AppVersionPlatform,
      });

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.APP_VERSIONS,
        option: OPTIONS.APP_VERSION_SET_CURRENT,
        actionCode: ACTION_CODE.WEB_APP_VER_SET_CURRENT,
        action: "When setting current app version",
        detail: `Set current app version ${target.platform} ${target.version}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The app version is now current.", "success");
      setSetCurrentAlert({ open: false });
      refetchAppVersions();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.appVersion;
    if (!target) return;

    try {
      await archiveAppVersion.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.APP_VERSIONS,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_APP_VER_DELETE,
        action: "When deleting an app version",
        detail: `Deleted app version ${target.platform} ${target.version}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchAppVersions();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "platform",
      isSortable: true,
      disablePadding: true,
      label: "Platform",
      align: "left",
      width: "140px",
    },
    {
      id: "version",
      isSortable: true,
      disablePadding: false,
      label: "Version",
      align: "left",
      width: "130px",
    },
    {
      id: "apiVersion",
      isSortable: false,
      disablePadding: false,
      label: "API",
      align: "left",
      width: "110px",
    },
    {
      id: "functionalVersion",
      isSortable: false,
      disablePadding: false,
      label: "Functional",
      align: "left",
      width: "120px",
    },
    {
      id: "isCurrent",
      isSortable: false,
      disablePadding: false,
      label: "Current",
      align: "left",
      width: "120px",
    },
    {
      id: "createdAt",
      isSortable: true,
      disablePadding: false,
      label: "Created",
      align: "left",
      width: "150px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: " ",
      align: "right",
      width: "80px",
    },
  ];

  const rows = useMemo(() => {
    if (!appVersions || appVersions.list.length === 0) return [];

    return appVersions.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        ...(!row.isCurrent
          ? [
              {
                value: "Make current",
                onClick: () =>
                  setSetCurrentAlert({ open: true, appVersion: row }),
                icon: <CheckCircleOutlineIcon fontSize="small" />,
              },
            ]
          : []),
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, appVersion: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      return createDataRow(index, {
        platform: <div>{PLATFORM_LABELS[row.platform] || row.platform}</div>,
        version: <div>{row.version}</div>,
        apiVersion: <div>{row.apiVersion || "-"}</div>,
        functionalVersion: <div>{row.functionalVersion || "-"}</div>,
        isCurrent: (
          <Chip
            sx={{
              bgcolor: row.isCurrent
                ? theme.palette.brand.light
                : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: row.isCurrent
                ? theme.palette.brand.main
                : theme.palette.warning.dark,
            }}
            label={
              <div className="text-xs">
                {row.isCurrent ? "Current" : "Not current"}
              </div>
            }
          />
        ),
        createdAt: (
          <div>
            {row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY") : "-"}
          </div>
        ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [appVersions, theme]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    App Versions
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <AppVersionFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={appVersions?.totalCount}
                  isLoading={isLoadingAppVersions || isFetchingAppVersions}
                  messageEmptyData="No app versions to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <AppVersionModal
          open={modalState.open}
          onClose={handleCloseModal}
          appVersion={modalState.appVersion}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {setCurrentAlert.open && (
        <GenericAlertModal
          onCancel={() => setSetCurrentAlert({ open: false })}
          onClick={handleConfirmSetCurrent}
          title="Make current"
          description={`Making "${setCurrentAlert.appVersion?.version}" the current ${PLATFORM_LABELS[setCurrentAlert.appVersion?.platform || ""] || setCurrentAlert.appVersion?.platform} version will replace whichever version is currently active for that platform. Continue?`}
          submitText="Make current"
          cancelText="Cancel"
          isLoading={setCurrentMutation.isPending}
          theme={theme}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveAppVersion.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default AppVersionsPage;
